/**
 * Horizontal Scroll Component
 * Unified solution for pinned horizontal scrolling
 */
class HorizontalScrollPinned {
	constructor(section) {
		this.section = section;
		this.leftTrackInner = section.querySelector(
			'[data-column="left"] .horizontal-scroll__track-inner'
		);
		this.rightTrackInner = section.querySelector(
			'[data-column="right"] .horizontal-scroll__track-inner'
		);

		this.isPinned = false;
		this.maxScrollDistance = 0;
		this.spacer = null;
		this.sectionTop = 0;

		this.init();
	}

	init() {
		this.calculateMaxScroll();
		this.createSpacer();
		this.bindEvents();

		// Delay section position calculation until layout is complete
		setTimeout(() => {
			this.updateSectionPosition();
		}, 100);
	}

	calculateMaxScroll() {
		// Calculate how far we can scroll horizontally for each column
		const leftScrollable = this.leftTrackInner
			? this.leftTrackInner.scrollWidth -
			  this.leftTrackInner.parentElement.clientWidth
			: 0;
		const rightScrollable = this.rightTrackInner
			? this.rightTrackInner.scrollWidth -
			  this.rightTrackInner.parentElement.clientWidth
			: 0;

		// Use the longer column to determine total scroll distance
		this.maxScrollDistance = Math.max(leftScrollable, rightScrollable);
	}

	createSpacer() {
		// Create a spacer element to provide scroll distance
		if (this.maxScrollDistance > 0) {
			this.spacer = document.createElement("div");
			const spacerHeight = this.maxScrollDistance;
			this.spacer.style.height = `${spacerHeight}px`;
			this.spacer.style.width = "100%";
			this.spacer.style.pointerEvents = "none";
			this.spacer.style.visibility = "hidden";
			this.spacer.className = "horizontal-scroll-spacer";

			// Insert spacer after the section
			this.section.parentNode.insertBefore(
				this.spacer,
				this.section.nextSibling
			);
		}
	}

	bindEvents() {
		window.addEventListener("scroll", () => this.handleScroll(), {
			passive: true,
		});
		window.addEventListener("resize", () => {
			this.calculateMaxScroll();
			this.updateSpacer();
			this.updateSectionPosition();
		});
	}

	updateSectionPosition() {
		const container = this.section.querySelector(
			".horizontal-scroll__container"
		);
		const newSectionTop = container
			? container.offsetTop
			: this.section.offsetTop;

		if (newSectionTop !== this.sectionTop) {
			this.sectionTop = newSectionTop;
		}
	}

	updateSpacer() {
		if (this.spacer && this.maxScrollDistance > 0) {
			const spacerHeight = this.maxScrollDistance;
			this.spacer.style.height = `${spacerHeight}px`;
		}
	}

	destroy() {
		if (this.spacer) {
			this.spacer.remove();
		}
	}

	handleScroll() {
		const rect = this.section.getBoundingClientRect();
		const scrollY = window.pageYOffset;

		if (this.sectionTop === 0) {
			this.updateSectionPosition();
		}

		const spacerHeight = this.spacer ? parseInt(this.spacer.style.height) : 0;
		const spacerStart = this.sectionTop + this.section.offsetHeight;
		const spacerEnd = spacerStart + spacerHeight;

		const scrollIntoSpacer = Math.max(0, scrollY - spacerStart);
		const progress =
			spacerHeight > 0 ? Math.min(scrollIntoSpacer / spacerHeight, 1) : 0;

		const shouldPin = rect.top <= 0 && progress < 1 && scrollY < spacerEnd;

		if (shouldPin !== this.isPinned) {
			this.isPinned = shouldPin;
			this.section.classList.toggle("is-pinned", shouldPin);
		}

		if (this.isPinned && this.maxScrollDistance > 0) {
			const translateX = -(progress * this.maxScrollDistance);
			const transform = `translateX(${translateX}px)`;

			if (this.leftTrackInner) {
				this.leftTrackInner.style.transform = transform;
			}
			if (this.rightTrackInner) {
				this.rightTrackInner.style.transform = transform;
			}
		} else if (!this.isPinned && progress >= 1) {
			const finalTransform = `translateX(-${this.maxScrollDistance}px)`;

			if (this.leftTrackInner) {
				this.leftTrackInner.style.transform = finalTransform;
			}
			if (this.rightTrackInner) {
				this.rightTrackInner.style.transform = finalTransform;
			}
		} else if (!this.isPinned && scrollY < this.sectionTop) {
			if (this.leftTrackInner) {
				this.leftTrackInner.style.transform = "";
			}
			if (this.rightTrackInner) {
				this.rightTrackInner.style.transform = "";
			}
		}
	}
}

// Store instances to avoid duplicate handlers
const scrollInstances = [];

// Initialize when ready
document.addEventListener("DOMContentLoaded", () => {
	const sections = document.querySelectorAll(".horizontal-scroll-section");

	// If there are multiple sections, treat them as one unified scroll experience
	if (sections.length > 1) {
		// Find the section with the most content to determine scroll distance
		let primarySection = null;
		let maxScrollDistance = 0;

		sections.forEach((section) => {
			const tempInstance = new HorizontalScrollPinned(section);

			if (tempInstance.maxScrollDistance > maxScrollDistance) {
				maxScrollDistance = tempInstance.maxScrollDistance;
				primarySection = section;
			}

			// Don't create spacer yet - only the primary section will have one
			if (tempInstance.spacer) {
				tempInstance.spacer.remove();
				tempInstance.spacer = null;
			}
		});

		if (primarySection) {
			const primaryInstance = new HorizontalScrollPinned(primarySection);
			scrollInstances.push(primaryInstance);
		}
	} else {
		// Single section - normal behavior
		sections.forEach((section) => {
			const instance = new HorizontalScrollPinned(section);
			scrollInstances.push(instance);
		});
	}
});
